import React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Alert,
  AlertTitle,
  Grid,
  Tooltip,
  IconButton,
  CircularProgress,
  useTheme,
  Stack,
  Divider,
} from "@mui/material";
import {
  AccountBalanceWalletOutlined,
  HourglassTopOutlined,
  ArrowUpwardOutlined,
  ArrowDownwardOutlined,
  ReceiptLongOutlined,
  WarningAmberOutlined,
  RefreshOutlined,
  AddCardOutlined,
  CurrencyExchangeOutlined,
  ShieldOutlined,
  InfoOutlined,
  PaymentsOutlined,
  CreditScoreOutlined,
  FlashOnOutlined,
} from "@mui/icons-material";
import { tokens } from "../../theme";

// Helper to format currency in KES
export const formatKES = (amount) => {
  const num = Number(amount) || 0;
  return `KES ${num.toLocaleString("en-KE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const WalletBalance = ({
  walletData,
  loading,
  onRefresh,
  onOpenTopUp,
  onOpenWithdraw,
  paybillNumber = "9617815",
  overdraftApprovedLimit = 0,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isDark = theme.palette.mode === "dark";

  const availableBalance = Number(walletData?.availableBalance || 0);
  const escrowedBalance = Number(walletData?.escrowedBalance || 0);
  const minRetainedBalance = Number(walletData?.minRetainedBalance || 0);
  const withdrawableBalance = Number(
    walletData?.withdrawableBalance !== undefined
      ? walletData.withdrawableBalance
      : Math.max(0, availableBalance - minRetainedBalance)
  );
  const totalDeposited = Number(walletData?.totalDeposited || 0);
  const totalDisbursed = Number(walletData?.totalDisbursed || 0);
  const totalFeesCharged = Number(walletData?.totalFeesCharged || 0);
  const totalSababishaCharges = Number(walletData?.totalSababishaCharges || 0);
  const totalMpesaCharges = Number(walletData?.totalMpesaCharges || 0);
  const totalRoyaltiesPaid = Number(walletData?.totalRoyaltiesPaid || 0);
  const lowBalanceThreshold = Number(walletData?.lowBalanceThreshold || 0);
  const lifecycleState = (walletData?.lifecycleState || "ACTIVE").toUpperCase();

  // Overdraft (Fuliza-style) values — headroom = remaining usable overdraft
  const overdraftHeadroom = Number(walletData?.overdraftHeadroom ?? 0);
  // approvedLimit may come as prop (from OverdraftPanel query) or estimated from headroom
  const resolvedOdLimit = Number(overdraftApprovedLimit || walletData?.overdraftApprovedLimit || 0);
  // If no approved limit info, show headroom only; utilisation bar uses it when limit > 0
  const overdraftUsed = resolvedOdLimit > 0 ? Math.max(0, resolvedOdLimit - overdraftHeadroom) : 0;
  const overdraftUtilPct =
    resolvedOdLimit > 0 ? Math.min(100, Math.round((overdraftUsed / resolvedOdLimit) * 100)) : 0;
  const hasOverdraft = overdraftHeadroom > 0 || resolvedOdLimit > 0;

  const isBelowThreshold =
    walletData?.isBelowThreshold !== undefined
      ? Boolean(walletData.isBelowThreshold)
      : lowBalanceThreshold > 0 && availableBalance <= lowBalanceThreshold;

  return (
    <Box sx={{ width: "100%", mb: 3 }}>
      {/* Dormancy Warning Banner */}
      {lifecycleState === "DORMANT" && (
        <Alert
          severity="error"
          variant="filled"
          icon={<WarningAmberOutlined fontSize="inherit" />}
          sx={{
            mb: 2,
            borderRadius: 2,
            backgroundColor: "#d32f2f",
            color: "#fff",
          }}
        >
          <AlertTitle sx={{ fontWeight: 700 }}>Company Account Dormant</AlertTitle>
          Disbursements and overdraft draws are blocked due to inactivity. Repayments continue to be accepted. Settle the activation fee in the Lifecycle tab to reactivate.
        </Alert>
      )}

      {/* Low Balance Alert Banner */}
      {isBelowThreshold && (
        <Alert
          severity="warning"
          variant="filled"
          icon={<WarningAmberOutlined fontSize="inherit" />}
          sx={{
            mb: 2.5,
            borderRadius: 2,
            boxShadow: "0 4px 20px rgba(237, 108, 2, 0.25)",
            backgroundColor: "#ed6c02",
            color: "#fff",
            alignItems: "center",
            "& .MuiAlert-message": {
              width: "100%",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 1.5,
            },
          }}
        >
          <Box>
            <AlertTitle sx={{ fontWeight: 700, mb: 0.5 }}>
              Low Wallet Balance Alert
            </AlertTitle>
            <Typography variant="body2" sx={{ opacity: 0.95 }}>
              Available balance ({formatKES(availableBalance)}) is at or below the minimum threshold ({formatKES(lowBalanceThreshold)}).
              Automated loan disbursements will be blocked if amount + fee exceeds available funds.
            </Typography>
          </Box>
          <Button
            variant="contained"
            size="small"
            onClick={onOpenTopUp}
            startIcon={<AddCardOutlined />}
            sx={{
              backgroundColor: "#fff",
              color: "#d32f2f",
              fontWeight: 700,
              textTransform: "none",
              px: 2,
              "&:hover": {
                backgroundColor: "#f5f5f5",
              },
            }}
          >
            Top Up Now
          </Button>
        </Alert>
      )}

      {/* Hero Balance Cards Grid */}
      <Grid container spacing={2.5}>
        {/* Main Available Balance Hero Card */}
        <Grid item xs={12} lg={7}>
          <Card
            sx={{
              height: "100%",
              borderRadius: 3,
              position: "relative",
              overflow: "hidden",
              background: isDark
                ? "linear-gradient(135deg, #172a3a 0%, #1a3c54 60%, #152232 100%)"
                : "linear-gradient(135deg, #1b537a 0%, #1766a2 60%, #0d3c61 100%)",
              color: "#ffffff",
              boxShadow: isDark
                ? "0 10px 30px rgba(0,0,0,0.4)"
                : "0 10px 30px rgba(23,102,162,0.25)",
              border: isDark ? "1px solid rgba(255,255,255,0.08)" : "none",
            }}
          >
            {/* Ambient background blur effect */}
            <Box
              sx={{
                position: "absolute",
                top: -40,
                right: -40,
                width: 180,
                height: 180,
                borderRadius: "50%",
                background: "radial-gradient(circle, rgba(122,217,170,0.2) 0%, transparent 70%)",
                filter: "blur(20px)",
                pointerEvents: "none",
              }}
            />

            <CardContent sx={{ p: { xs: 2.5, sm: 3.5 } }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  mb: 2,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Box
                    sx={{
                      p: 1.2,
                      borderRadius: 2,
                      backgroundColor: "rgba(255, 255, 255, 0.15)",
                      backdropFilter: "blur(8px)",
                      display: "flex",
                    }}
                  >
                    <AccountBalanceWalletOutlined sx={{ fontSize: 28, color: "#7ad9aa" }} />
                  </Box>
                  <Box>
                    <Typography
                      variant="overline"
                      sx={{
                        fontSize: "0.75rem",
                        letterSpacing: "1.2px",
                        color: "rgba(255,255,255,0.75)",
                        fontWeight: 600,
                      }}
                    >
                      COMPANY SPENDABLE BALANCE
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: "#fff", lineHeight: 1.2 }}>
                      Available for Disbursements
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Chip
                    icon={<ShieldOutlined sx={{ fontSize: "16px !important", color: "#7ad9aa" }} />}
                    label={`Paybill: ${paybillNumber}`}
                    size="small"
                    sx={{
                      backgroundColor: "rgba(255,255,255,0.12)",
                      color: "#fff",
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      backdropFilter: "blur(6px)",
                    }}
                  />
                  <Tooltip title="Refresh balance">
                    <IconButton
                      onClick={onRefresh}
                      size="small"
                      sx={{
                        color: "rgba(255,255,255,0.8)",
                        backgroundColor: "rgba(255,255,255,0.1)",
                        "&:hover": { backgroundColor: "rgba(255,255,255,0.2)" },
                      }}
                    >
                      {loading ? (
                        <CircularProgress size={18} sx={{ color: "#fff" }} />
                      ) : (
                        <RefreshOutlined fontSize="small" />
                      )}
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              {/* Amount Display */}
              <Box sx={{ my: 2.5 }}>
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 800,
                    fontSize: { xs: "2rem", sm: "2.8rem" },
                    letterSpacing: "-0.5px",
                    color: "#ffffff",
                    fontFamily: "Outfit, Inter, sans-serif",
                  }}
                >
                  {formatKES(availableBalance)}
                </Typography>

                {/* Withdrawable vs Retained Reserve breakdown  */}
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, my: 1 }}>
                  <Chip
                    label={`Withdrawable: ${formatKES(withdrawableBalance)}`}
                    size="small"
                    sx={{
                      backgroundColor: "rgba(34, 191, 114, 0.2)",
                      color: "#7ad9aa",
                      fontWeight: 700,
                      fontSize: "0.78rem",
                      backdropFilter: "blur(6px)",
                    }}
                  />
                  {minRetainedBalance > 0 && (
                    <Chip
                      label={`Retained Reserve: ${formatKES(minRetainedBalance)}`}
                      size="small"
                      sx={{
                        backgroundColor: "rgba(255, 255, 255, 0.12)",
                        color: "rgba(255,255,255,0.85)",
                        fontWeight: 600,
                        fontSize: "0.75rem",
                      }}
                    />
                  )}
                </Box>

                {/* ── Overdraft / Fuliza-style strip ─────────────────────── */}
                {hasOverdraft && (
                  <Box
                    sx={{
                      mt: 2,
                      p: 1.8,
                      borderRadius: 2,
                      background: "rgba(255,255,255,0.07)",
                      backdropFilter: "blur(8px)",
                      border: "1px solid rgba(255,255,255,0.14)",
                    }}
                  >
                    {/* Title row */}
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.8 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                        <FlashOnOutlined sx={{ fontSize: 16, color: "#ffd54f" }} />
                        <Typography
                          variant="caption"
                          sx={{ fontWeight: 800, color: "#ffd54f", letterSpacing: "0.8px", fontSize: "0.72rem" }}
                        >
                          OVERDRAFT AVAILABLE (FULIZA)
                        </Typography>
                      </Box>
                      <Chip
                        icon={<CreditScoreOutlined sx={{ fontSize: "14px !important", color: overdraftHeadroom > 0 ? "#7ad9aa" : "rgba(255,255,255,0.5)" }} />}
                        label={overdraftHeadroom > 0 ? "Active" : "Fully Drawn"}
                        size="small"
                        sx={{
                          height: 10,
                          fontSize: "0.68rem",
                          fontWeight: 700,
                          backgroundColor: overdraftHeadroom > 0
                            ? "rgba(34, 191, 114, 0.18)"
                            : "rgba(211,47,47,0.18)",
                          color: overdraftHeadroom > 0 ? "#7ad9aa" : "#ef9a9a",
                          "& .MuiChip-icon": { ml: "4px" },
                        }}
                      />
                    </Box>

                    {/* Big overdraft headroom number */}
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 800,
                        fontSize: { xs: "1.5rem", sm: "1.9rem" },
                        color: overdraftHeadroom > 0 ? "#ffd54f" : "rgba(255,255,255,0.4)",
                        fontFamily: "Outfit, Inter, sans-serif",
                        letterSpacing: "-0.3px",
                        mb: 0.5,
                      }}
                    >
                      {formatKES(overdraftHeadroom)}
                    </Typography>

                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.6)", fontSize: "0.72rem" }}>
                      You can use up to this amount as overdraft buffer for disbursements
                    </Typography>

                    {/* Utilisation progress bar — only when we know the full limit */}
                    {resolvedOdLimit > 0 && (
                      <Box sx={{ mt: 1.2 }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.4 }}>
                          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.55)", fontSize: "0.68rem" }}>
                            Used: {formatKES(overdraftUsed)}
                          </Typography>
                          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.55)", fontSize: "0.68rem" }}>
                            Limit: {formatKES(resolvedOdLimit)}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            height: 6,
                            borderRadius: 3,
                            backgroundColor: "rgba(255,255,255,0.12)",
                            overflow: "hidden",
                          }}
                        >
                          <Box
                            sx={{
                              height: "100%",
                              width: `${overdraftUtilPct}%`,
                              borderRadius: 3,
                              background:
                                overdraftUtilPct > 80
                                  ? "linear-gradient(90deg, #f44336, #e53935)"
                                  : overdraftUtilPct > 50
                                    ? "linear-gradient(90deg, #ffa726, #ff9800)"
                                    : "linear-gradient(90deg, #66bb6a, #43a047)",
                              transition: "width 0.6s ease",
                            }}
                          />
                        </Box>
                        <Typography
                          variant="caption"
                          sx={{ color: "rgba(255,255,255,0.45)", fontSize: "0.66rem", mt: 0.3, display: "block" }}
                        >
                          {overdraftUtilPct}% of facility used · repaid automatically from inflows
                        </Typography>
                      </Box>
                    )}
                  </Box>
                )}

                <Typography
                  variant="caption"
                  sx={{
                    color: "rgba(255,255,255,0.7)",
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    mt: 1.2,
                  }}
                >
                  <InfoOutlined sx={{ fontSize: 13 }} />
                  Funds in Sababisha Global shortcode, isolated by company tenant.
                </Typography>
              </Box>

              <Divider sx={{ borderColor: "rgba(255,255,255,0.15)", my: 2 }} />

              {/* Action Buttons */}
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 1.5,
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Stack direction="row" spacing={1.5}>
                  <Button
                    variant="contained"
                    onClick={onOpenTopUp}
                    startIcon={<AddCardOutlined />}
                    sx={{
                      backgroundColor: "#22bf72",
                      color: "#ffffff",
                      fontWeight: 700,
                      textTransform: "none",
                      px: 2.5,
                      py: 1,
                      borderRadius: 2,
                      boxShadow: "0 4px 14px rgba(34, 191, 114, 0.4)",
                      "&:hover": {
                        backgroundColor: "#1b995b",
                      },
                    }}
                  >
                    Top Up Wallet
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={onOpenWithdraw}
                    startIcon={<CurrencyExchangeOutlined />}
                    sx={{
                      borderColor: "rgba(255,255,255,0.5)",
                      color: "#ffffff",
                      fontWeight: 600,
                      textTransform: "none",
                      px: 2.5,
                      py: 1,
                      borderRadius: 2,
                      backdropFilter: "blur(6px)",
                      "&:hover": {
                        borderColor: "#ffffff",
                        backgroundColor: "rgba(255,255,255,0.1)",
                      },
                    }}
                  >
                    Withdraw Funds
                  </Button>
                </Stack>

                {lowBalanceThreshold > 0 && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: isBelowThreshold ? "#ffd54f" : "rgba(255,255,255,0.7)",
                      fontWeight: 500,
                    }}
                  >
                    Min Alert Limit: {formatKES(lowBalanceThreshold)}
                  </Typography>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Escrow & Security Status Card */}
        <Grid item xs={12} lg={5}>
          <Card
            sx={{
              height: "100%",
              borderRadius: 3,
              backgroundColor: isDark ? colors.primary[400] : "#ffffff",
              border: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.08)"}`,
              boxShadow: isDark
                ? "0 4px 20px rgba(0,0,0,0.2)"
                : "0 4px 20px rgba(0,0,0,0.05)",
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
            }}
          >
            <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 1.5,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box
                    sx={{
                      p: 1,
                      borderRadius: 2,
                      backgroundColor: isDark
                        ? "rgba(255, 179, 0, 0.15)"
                        : "rgba(237, 108, 2, 0.1)",
                      display: "flex",
                    }}
                  >
                    <HourglassTopOutlined
                      sx={{
                        fontSize: 22,
                        color: isDark ? "#ffb300" : "#ed6c02",
                      }}
                    />
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: colors.grey[100] }}>
                    In-Flight Escrow
                  </Typography>
                </Box>
                <Chip
                  label="Atomic Lock"
                  size="small"
                  sx={{
                    fontSize: "0.7rem",
                    fontWeight: 700,
                    backgroundColor: isDark ? "rgba(255,255,255,0.08)" : "#f0f4f8",
                    color: colors.grey[300],
                  }}
                />
              </Box>

              <Typography variant="body2" sx={{ color: colors.grey[400], mb: 2 }}>
                Funds currently reserved for disbursements awaiting Safaricom B2C callbacks.
              </Typography>

              {/* Escrow Amount */}
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  backgroundColor: isDark ? "rgba(0,0,0,0.25)" : "#f8fafc",
                  border: `1px solid ${isDark ? "rgba(255,255,255,0.05)" : "#e2e8f0"}`,
                  display: "flex",
                  alignItems: "baseline",
                  justifyContent: "space-between",
                }}
              >
                <Box>
                  <Typography variant="caption" sx={{ color: colors.grey[400], fontWeight: 600 }}>
                    LOCKED ESCROW AMOUNT
                  </Typography>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 800,
                      color: escrowedBalance > 0 ? (isDark ? "#ffb300" : "#d97706") : colors.grey[200],
                      fontFamily: "Outfit, Inter, sans-serif",
                    }}
                  >
                    {formatKES(escrowedBalance)}
                  </Typography>
                </Box>
                <Chip
                  label={escrowedBalance > 0 ? "Pending Settlement" : "No Pending Escrow"}
                  size="small"
                  color={escrowedBalance > 0 ? "warning" : "default"}
                  variant="outlined"
                  sx={{ fontWeight: 600, fontSize: "0.7rem" }}
                />
              </Box>

              {/* Architecture Invariant Note */}
              <Box sx={{ mt: 2.5, display: "flex", alignItems: "flex-start", gap: 1 }}>
                <ShieldOutlined sx={{ fontSize: 18, color: "#1f76ad", mt: 0.2 }} />
                <Typography variant="caption" sx={{ color: colors.grey[400], lineHeight: 1.4 }}>
                  <strong>Escrow-Then-Send Guarantee:</strong> Funds are atomicity held in database row lock prior to M-Pesa dispatch. On B2C callback confirmation, escrow commits; on failure, funds auto-release.
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Lifetime Financial Totals Metric Row */}
      <Grid container spacing={2} sx={{ mt: 0.5 }}>
        {/* Total Deposited */}
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              borderRadius: 2.5,
              backgroundColor: isDark ? colors.primary[400] : "#ffffff",
              border: `1px solid ${isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.06)"}`,
              boxShadow: isDark ? "none" : "0 2px 8px rgba(0,0,0,0.04)",
            }}
          >
            <CardContent sx={{ p: 2.2 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                <Typography variant="caption" sx={{ color: colors.grey[400], fontWeight: 700, letterSpacing: "0.5px" }}>
                  LIFETIME DEPOSITED
                </Typography>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: isDark ? "rgba(34,191,114,0.15)" : "#e8f8f0",
                    color: "#22bf72",
                  }}
                >
                  <ArrowDownwardOutlined fontSize="small" />
                </Box>
              </Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: colors.grey[100] }}>
                {formatKES(totalDeposited)}
              </Typography>
              <Typography variant="caption" sx={{ color: colors.grey[400] }}>
                Cumulative STK & manual top-ups
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Total Disbursed */}
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              borderRadius: 2.5,
              backgroundColor: isDark ? colors.primary[400] : "#ffffff",
              border: `1px solid ${isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.06)"}`,
              boxShadow: isDark ? "none" : "0 2px 8px rgba(0,0,0,0.04)",
            }}
          >
            <CardContent sx={{ p: 2.2 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                <Typography variant="caption" sx={{ color: colors.grey[400], fontWeight: 700, letterSpacing: "0.5px" }}>
                  LIFETIME DISBURSED
                </Typography>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: isDark ? "rgba(31,118,173,0.15)" : "#e8f4fc",
                    color: "#1f76ad",
                  }}
                >
                  <ArrowUpwardOutlined fontSize="small" />
                </Box>
              </Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: colors.grey[100] }}>
                {formatKES(totalDisbursed)}
              </Typography>
              <Typography variant="caption" sx={{ color: colors.grey[400] }}>
                Total successful B2C payouts
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Total Fees Charged */}
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              borderRadius: 2.5,
              backgroundColor: isDark ? colors.primary[400] : "#ffffff",
              border: `1px solid ${isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.06)"}`,
              boxShadow: isDark ? "none" : "0 2px 8px rgba(0,0,0,0.04)",
            }}
          >
            <CardContent sx={{ p: 2.2 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                <Typography variant="caption" sx={{ color: colors.grey[400], fontWeight: 700, letterSpacing: "0.5px" }}>
                  PLATFORM FEES
                </Typography>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: isDark ? "rgba(156,39,176,0.15)" : "#f3e5f5",
                    color: "#9c27b0",
                  }}
                >
                  <ReceiptLongOutlined fontSize="small" />
                </Box>
              </Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: colors.grey[100] }}>
                {formatKES(totalFeesCharged)}
              </Typography>
              <Typography variant="caption" sx={{ color: colors.grey[400] }}>
                Disbursement processing fees
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Total Royalties Paid */}
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              borderRadius: 2.5,
              backgroundColor: isDark ? colors.primary[400] : "#ffffff",
              border: `1px solid ${isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.06)"}`,
              boxShadow: isDark ? "none" : "0 2px 8px rgba(0,0,0,0.04)",
            }}
          >
            <CardContent sx={{ p: 2.2 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                <Typography variant="caption" sx={{ color: colors.grey[400], fontWeight: 700, letterSpacing: "0.5px" }}>
                  WITHDRAWAL ROYALTIES
                </Typography>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: isDark ? "rgba(237,108,2,0.15)" : "#fff3e0",
                    color: "#ed6c02",
                  }}
                >
                  <PaymentsOutlined fontSize="small" />
                </Box>
              </Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: colors.grey[100] }}>
                {formatKES(totalRoyaltiesPaid)}
              </Typography>
              <Typography variant="caption" sx={{ color: colors.grey[400] }}>
                Total withdrawal charges
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default WalletBalance;
